import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { hostingService } from '../services/hostingService';
import DashShell from '../components/dash/DashShell';

interface VPSSubscription {
  id: string;
  package_id: string;
  status: string;
  billing_cycle: string;
  price: number;
  start_date: string;
  end_date: string;
  package_name?: string;
  package_details?: any;
}

const VPSDashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const [vpsSubscriptions, setVpsSubscriptions] = useState<VPSSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchVPSSubscriptions();
    }
  }, [user]);

  const fetchVPSSubscriptions = async () => {
    try {
      const result = await hostingService.getSubscriptions();
      if (result.success) {
        // Filter for VPS subscriptions only
        const vpsOnly = result.data.filter((sub: any) => 
          sub.package_details?.type === 'vps' || 
          sub.package_name?.toLowerCase().includes('vps')
        );
        setVpsSubscriptions(vpsOnly);
      }
    } catch (error) {
      console.error('Error fetching VPS subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDeployVPS = (subscription: VPSSubscription) => {
    // TODO: Implement VPS deployment modal/flow
    alert(`VPS deployment for ${subscription.package_name || 'VPS'} will be implemented here. This will include server configuration, OS selection, and initial setup.`);
  };

  const handleManageVPS = (subscription: VPSSubscription) => {
    // TODO: Implement VPS management modal/flow
    alert(`VPS management for ${subscription.package_name || 'VPS'} will be implemented here. This will include power controls, monitoring, backups, etc.`);
  };

  if (authLoading || loading) {
    return (
      <DashShell title="VPS" subtitle="Loading your virtual private servers…">
        <div className="h-40 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }
  if (!user) {
    return (
      <DashShell title="VPS" subtitle="Sign in required">
        <div className="rounded-2xl bg-white/80 backdrop-blur border border-white/40 p-8 text-center shadow-sm">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Please log in</h2>
          <p className="text-slate-600">You need to be signed in to view your VPS dashboard.</p>
        </div>
      </DashShell>
    );
  }

  const browseCta = vpsSubscriptions.length === 0 ? null : (
    <a
      href="/vps"
      className="text-sm bg-white/80 backdrop-blur border border-white/40 text-primary-navy px-3 py-1.5 rounded-lg hover:border-secondary-500 transition"
    >
      Browse VPS plans →
    </a>
  );

  return (
    <DashShell
      title="VPS"
      subtitle="Manage and deploy your Virtual Private Servers"
      right={browseCta}
    >
      {vpsSubscriptions.length === 0 ? (
        <div className="bg-white/80 backdrop-blur border border-white/40 rounded-2xl p-8 text-center shadow-sm max-w-xl mx-auto">
          <div className="text-4xl mb-3">🖥️</div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No VPS yet</h3>
          <p className="text-sm text-slate-600 mb-5 max-w-md mx-auto">
            Pick a plan to spin up a Linux VPS — choose CPU/RAM, OS template, and region.
            Provisioning takes a few minutes.
          </p>
          <a
            href="/vps"
            className="inline-block bg-secondary-500 text-primary-navy font-medium px-5 py-2 rounded-lg hover:bg-secondary-600 transition"
          >
            Browse VPS plans →
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vpsSubscriptions.map((subscription) => (
            <div
              key={subscription.id}
              className="bg-white/80 backdrop-blur border border-white/40 rounded-2xl shadow-sm overflow-hidden"
            >
              <div className="bg-gradient-to-r from-primary-navy via-primary-700 to-secondary-500 text-white p-5">
                <h3 className="text-lg font-semibold mb-2">
                  {subscription.package_name || 'VPS Server'}
                </h3>
                <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(subscription.status)}`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </div>

              <div className="p-5">
                <div className="space-y-2 mb-5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Billing</span>
                    <span className="font-medium text-slate-900">${subscription.price}/{subscription.billing_cycle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Next billing</span>
                    <span className="font-medium text-slate-900">
                      {new Date(subscription.end_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {subscription.status === 'active' ? (
                  <div className="space-y-2">
                    <button
                      onClick={() => handleDeployVPS(subscription)}
                      className="w-full bg-primary-navy text-white py-2 px-4 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                    >
                      Deploy VPS
                    </button>
                    <button
                      onClick={() => handleManageVPS(subscription)}
                      className="w-full bg-secondary-500 text-primary-navy py-2 px-4 rounded-lg font-medium hover:bg-secondary-600 transition-colors"
                    >
                      Manage VPS
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-slate-500 text-sm">
                      VPS is {subscription.status}. Contact support for assistance.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashShell>
  );
};

export default VPSDashboard;